import { useDeleteEvents, useGetSchedulersEvents } from '../../api-http/event';
import { DialogModal } from '../../components';
import { AlertBar } from '../../components/AlertBar';
import {
  StyledTableCell,
  StyledTableSortLabel,
} from '../../components/TableTheme';
import { notifyMsg } from '../../configs';
import { EventEditResponse } from '../../types/event.types';
import { OrderEnum, SortEnum } from '../../types/pagination.types';
import { UserRole } from '../../types/user.types';
import { displayDate } from '../../util/dateTime';
import { frequency } from '../../util/frequency';
import { useIsUserRole } from '../../util/useIsUserRole';
import { useSnack } from '../../util/useSnack';
import { useTable } from '../../util/useTable';
import { SchedulerEditForm } from './SchedulerEditForm';
import {
  IconButton,
  LinearProgress,
  Stack,
  TablePagination,
  Tooltip,
} from '@mui/material';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { useConfirm } from 'material-ui-confirm';
import { FC, useEffect, useState } from 'react';
import { Edit3, Trash2 } from 'react-feather';
import { useParams } from 'react-router-dom';
import { managementTableSx } from '../../components/ManagementPageLayout';

interface EventTableProp {
  refresh: boolean;
}

const actionButtonSx = {
  width: 32,
  height: 32,
  border: '1px solid #dbe3ee',
  borderRadius: 2,
};

export const EventTable: FC<EventTableProp> = ({ refresh }) => {
  const params = useParams<{ id: string }>();
  const confirm = useConfirm();
  const [openSchedule, setOpenSchedule] = useState(false);
  const [schedulerId, setSchedulerId] = useState('');
  const disableDelete = useIsUserRole([UserRole.user, UserRole.dataEditor]);
  const { showErrSnack, showSuccessSnack } = useSnack();
  const { sortOnClick, pagingParams, tableProps } = useTable();
  const { sortby, order } = pagingParams;

  const {
    data: list,
    isLoading: listIsLoading,
    refetch,
    error,
  } = useGetSchedulersEvents({
    ...pagingParams,
    scheduler: params.id,
  });

  const {
    error: deleteError,
    mutate: deleteEvent,
    data: deletedEvent,
  } = useDeleteEvents({
    onSuccess: () => showSuccessSnack(notifyMsg.EVENT_DELETE_SUCCESS),
    onError: () => showErrSnack(notifyMsg.DELETE_ATTEMPT_FAILED),
  });

  const [activeEvent, setActiveEvent] = useState<
    EventEditResponse | undefined
  >();

  useEffect(() => {
    refetch();
  }, [deletedEvent, refresh, tableProps]);

  const handleDelete = async (id: string) => {
    try {
      await confirm({
        description: notifyMsg.DELETE_CONFIRMATION,
        confirmationText: 'Yes',
      });
      deleteEvent({ id });
    } catch (e) {}
  };

  return (
    <>
      {listIsLoading && <LinearProgress />}
      <AlertBar severity="error" msg={error?.msg || deleteError?.msg} />
      <TableContainer component={Paper} sx={managementTableSx}>
        <Table sx={{ minWidth: 900 }} aria-label="Schedule events">
          <TableHead>
            <TableRow>
              <StyledTableCell align="left" width={200}>
                <StyledTableSortLabel
                  active={sortby === SortEnum.presentationName || true}
                  direction={
                    sortby === SortEnum.presentationName ? order : OrderEnum.asc
                  }
                  onClick={() => sortOnClick(SortEnum.presentationName)}
                >
                  Presentation
                </StyledTableSortLabel>
              </StyledTableCell>

              <StyledTableCell align="left">
                <StyledTableSortLabel
                  active={sortby === SortEnum.startDate || true}
                  direction={
                    sortby === SortEnum.startDate ? order : OrderEnum.asc
                  }
                  onClick={() => sortOnClick(SortEnum.startDate)}
                >
                  Start date
                </StyledTableSortLabel>
              </StyledTableCell>
              <StyledTableCell align="left">
                <StyledTableSortLabel
                  active={sortby === SortEnum.endDate || true}
                  direction={
                    sortby === SortEnum.endDate ? order : OrderEnum.asc
                  }
                  onClick={() => sortOnClick(SortEnum.endDate)}
                >
                  End date
                </StyledTableSortLabel>
              </StyledTableCell>
              <StyledTableCell align="left">Start time</StyledTableCell>

              <StyledTableCell align="left">End time</StyledTableCell>
              <StyledTableCell align="left">Frequency</StyledTableCell>
              <StyledTableCell align="center">Actions</StyledTableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {list?.data?.map((eventScheduler, index) => {
              return (
                <TableRow
                  key={index}
                  sx={{ height: 56 }}
                  id={eventScheduler._id}
                  onClick={() => {
                    setSchedulerId(eventScheduler._id);
                  }}
                >
                  <TableCell align="left">
                    {eventScheduler?.presentation.name}
                  </TableCell>
                  <TableCell align="left">
                    {' '}
                    {displayDate(eventScheduler?.presentationSchedule.dtstart)}
                  </TableCell>
                  <TableCell align="left">
                    {displayDate(eventScheduler?.presentationSchedule.until)}
                  </TableCell>
                  <TableCell align="left">
                    {eventScheduler?.startTime}
                  </TableCell>

                  <TableCell align="left">{eventScheduler?.endTime}</TableCell>
                  <TableCell align="left">
                    {frequency(
                      eventScheduler?.presentationSchedule.freq,
                      eventScheduler?.presentationSchedule.byweekday,
                      eventScheduler?.presentationSchedule.bysetpos,
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <Stack
                      direction="row"
                      justifyContent="center"
                      spacing={0.75}
                    >
                      <Tooltip title="Edit event">
                        <span>
                          <IconButton
                            aria-label={`Edit event for ${eventScheduler.presentation.name}`}
                            disabled={disableDelete}
                            onClick={() => {
                              setActiveEvent(eventScheduler);
                              setSchedulerId(eventScheduler._id);
                              setOpenSchedule(true);
                            }}
                            sx={{ ...actionButtonSx, color: '#2563eb' }}
                          >
                            <Edit3 size={15} />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Delete event">
                        <span>
                          <IconButton
                            aria-label={`Delete event for ${eventScheduler.presentation.name}`}
                            disabled={disableDelete}
                            onClick={() => handleDelete(eventScheduler._id)}
                            sx={{
                              ...actionButtonSx,
                              color: '#ef4444',
                              borderColor: '#fecaca',
                              bgcolor: '#fffafa',
                            }}
                          >
                            <Trash2 size={15} />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        <TablePagination
          {...tableProps}
          component="div"
          hidden={list?.pagination.total === 0}
          count={list?.pagination.total || 0}
        />
      </TableContainer>
      <DialogModal
        title="Edit event"
        onClose={() => setOpenSchedule(false)}
        open={openSchedule}
        width={560}
      >
        <SchedulerEditForm
          onFinished={() => setOpenSchedule(false)}
          schedulerId={schedulerId || ''}
          event={list && activeEvent}
        />
      </DialogModal>
    </>
  );
};
export default EventTable;
